import { useState, type DragEvent } from "react";
import { Button, Input, Col, Row, Empty } from "antd";
import {
    ArrowLeftOutlined,
    DownloadOutlined,
    PlusOutlined,
    CloseOutlined,
    MenuOutlined,
    CheckCircleOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import Papa from "papaparse";
import moment from "moment";

import { Client } from "../../core/api-client";
import {
    API_MILESTONES_LIST,
    ROUTE_MILESTONES,
} from "../../shared/routes/private.routes";
import {
    formatCost1,
    showNotification,
} from "../../shared/utils/utils";

import "./export-milestones.less";

const MILESTONE_EXPORT_COLUMNS = [
    { key: "accountName", label: "Client" },
    { key: "sowNumber", label: "SoW#" },
    { key: "poNumber", label: "PO Number" },
    { key: "projectName", label: "Project" },
    { key: "spocName", label: "SPOC" },
    { key: "name", label: "Milestone" },
    { key: "percentage", label: "%Age" },
    { key: "poCurrencyName", label: "PO Currency" },
    { key: "poValue", label: "PO Value" },
    { key: "milestoneValue", label: "Milestone Value" },
    { key: "estimatedBillingDate", label: "Estimated Billing Date" },
    { key: "invoiceStatus", label: "Invoice Status" },
    { key: "invoiceDate", label: "Invoice Date" },
    { key: "invoiceDueDate", label: "Due Date" },
    { key: "invoiceNumber", label: "Invoice No." },
    { key: "paymentReceived", label: "Payment Received" },
    { key: "paymentReceivedDate", label: "Payment Receipt Date" },
];

type DragSource = "available" | "active";

const ExportMilestonesPage = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Filters and sorting received from Manage Milestones.
    const filters = location.state?.filters || {};

    const sorter = location.state?.sorter || {
        sort_by: "order",
        sort_type: "ASC",
    };

    const [activeExportColumns, setActiveExportColumns] = useState<string[]>(
        MILESTONE_EXPORT_COLUMNS.map((column) => column.key)
    );

    const [availableSearch, setAvailableSearch] = useState("");
    const [activeSearch, setActiveSearch] = useState("");
    const [exporting, setExporting] = useState(false);

    const [draggedColumnKey, setDraggedColumnKey] = useState<string | null>(
        null
    );
    const [dragSource, setDragSource] = useState<DragSource | null>(null);

    const availableExportColumns = MILESTONE_EXPORT_COLUMNS.filter(
        (column) => !activeExportColumns.includes(column.key)
    );

    const filteredAvailableExportColumns = availableExportColumns.filter(
        (column) =>
            column.label.toLowerCase().includes(availableSearch.toLowerCase())
    );

    const filteredActiveExportColumns = activeExportColumns
        .map((key) =>
            MILESTONE_EXPORT_COLUMNS.find((column) => column.key === key)
        )
        .filter(
            (column): column is (typeof MILESTONE_EXPORT_COLUMNS)[number] =>
                Boolean(
                    column &&
                    column.label.toLowerCase().includes(activeSearch.toLowerCase())
                )
        );

    const addExportColumn = (key: string) => {
        setActiveExportColumns((previous) =>
            previous.includes(key) ? previous : [...previous, key]
        );
    };

    const removeExportColumn = (key: string) => {
        setActiveExportColumns((previous) =>
            previous.filter((columnKey) => columnKey !== key)
        );
    };

    const clearDragState = () => {
        setDraggedColumnKey(null);
        setDragSource(null);
    };

    const handleDragStart = (
        event: DragEvent<HTMLDivElement>,
        key: string,
        source: DragSource
    ) => {
        setDraggedColumnKey(key);
        setDragSource(source);

        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", key);
    };

    const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
    };

    const handleDropOnActive = (
        event: DragEvent<HTMLDivElement>,
        targetKey?: string
    ) => {
        event.preventDefault();

        const sourceKey =
            draggedColumnKey || event.dataTransfer.getData("text/plain");

        if (!sourceKey) {
            clearDragState();
            return;
        }

        setActiveExportColumns((previous) => {
            const updated = [...previous];

            if (dragSource === "available") {
                if (updated.includes(sourceKey)) return previous;

                const targetIndex = targetKey
                    ? updated.indexOf(targetKey)
                    : updated.length;

                if (targetIndex === -1) {
                    return [...updated, sourceKey];
                }

                updated.splice(targetIndex, 0, sourceKey);
                return updated;
            }

            if (dragSource === "active") {
                const sourceIndex = updated.indexOf(sourceKey);
                const targetIndex = targetKey
                    ? updated.indexOf(targetKey)
                    : updated.length - 1;

                if (sourceIndex === -1 || targetIndex === -1) {
                    return previous;
                }

                const [movedColumn] = updated.splice(sourceIndex, 1);
                updated.splice(targetIndex, 0, movedColumn);

                return updated;
            }

            return previous;
        });

        clearDragState();
    };

    const handleDropOnAvailable = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();

        const sourceKey =
            draggedColumnKey || event.dataTransfer.getData("text/plain");

        if (sourceKey && dragSource === "active") {
            removeExportColumn(sourceKey);
        }

        clearDragState();
    };

    const selectAllExportColumns = () => {
        setActiveExportColumns(
            MILESTONE_EXPORT_COLUMNS.map((column) => column.key)
        );
    };

    const removeAllExportColumns = () => {
        setActiveExportColumns([]);
    };

    const formatDate = (value: any) =>
        value ? moment(value).format("DD-MMM-YYYY") : "";

    const mapMilestoneRow = (item: any) => {
        const milestoneValue =
            item?.milestoneAmount != null
                ? formatCost1(
                    Number(item.milestoneAmount),
                    item?.poCurrencyName || ""
                )
                : item?.percentage != null && item?.poValue != null
                    ? formatCost1(
                        (Number(item.poValue) * Number(item.percentage)) / 100,
                        item?.poCurrencyName || ""
                    )
                    : "";

        return {
            accountName: item?.accountName ?? "",
            sowNumber: item?.sowNumber ?? "",
            poNumber: item?.poNumber ?? "",
            projectName: item?.projectName ?? "",
            spocName: item?.spocName ?? "",
            name: item?.name ?? "",
            percentage:
                item?.percentage != null
                    ? Number(item.percentage).toFixed(4)
                    : "",
            poCurrencyName: item?.poCurrencyName ?? "",
            poValue:
                item?.poValue != null
                    ? formatCost1(Number(item.poValue), item?.poCurrencyName || "")
                    : "",
            milestoneValue,
            estimatedBillingDate: formatDate(item?.estimatedBillingDate),
            invoiceStatus: item?.invoiceStatus ?? "",
            invoiceDate: formatDate(item?.invoiceDate),
            invoiceDueDate: formatDate(item?.invoiceDueDate),
            invoiceNumber: item?.invoiceNumber ?? "",
            paymentReceived: item?.paymentReceived ? "Yes" : "No",
            paymentReceivedDate: formatDate(item?.paymentReceivedDate),
        };
    };

    const exportRows = async () => {
        if (activeExportColumns.length === 0) {
            showNotification("Please select at least one column", "error");
            return;
        }

        setExporting(true);

        try {
            // Remove empty filter values but preserve false for Payment Received.
            const exportFilters = Object.fromEntries(
                Object.entries(filters).filter(
                    ([, value]) =>
                        value !== undefined &&
                        value !== null &&
                        value !== "" &&
                        !(Array.isArray(value) && value.length === 0)
                )
            );

            // Do not send page or limit: export all matching milestones.
            const response = await Client.post(API_MILESTONES_LIST, {
                ...exportFilters,
                sort_by: sorter.sort_by || "order",
                sort_type: sorter.sort_type || "ASC",
            });

            const rows = response?.data?.data;

            if (!Array.isArray(rows)) {
                throw new Error("Invalid Milestones API response");
            }

            if (rows.length === 0) {
                showNotification("No milestone records found", "warning");
                return;
            }

            const selectedColumns = activeExportColumns
                .map((key) =>
                    MILESTONE_EXPORT_COLUMNS.find((column) => column.key === key)
                )
                .filter(
                    (column): column is (typeof MILESTONE_EXPORT_COLUMNS)[number] =>
                        Boolean(column)
                );

            const mappedRows = rows.map(mapMilestoneRow);

            const csvRows = mappedRows.map((row: any) =>
                Object.fromEntries(
                    selectedColumns.map((column) => [
                        column.label,
                        row[column.key] ?? "",
                    ])
                )
            );

            const csv = Papa.unparse(csvRows, {
                escapeFormulae: true,
                columns: selectedColumns.map((column) => column.label),
            });

            const blob = new Blob(["\uFEFF", csv], {
                type: "text/csv;charset=utf-8;",
            });

            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = url;
            link.download = "milestones.csv";

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            URL.revokeObjectURL(url);

            showNotification(
                `Successfully exported ${rows.length} Milestone records`,
                "success"
            );
        } catch (error) {
            console.error("Milestones export failed:", error);
            showNotification("Unable to export Milestone records", "error");
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="app-page milestone-export-page">
            <div className="milestone-export-page-header">
                <div className="milestone-export-page-heading">
                    <div className="milestone-export-page-icon">
                        <DownloadOutlined />
                    </div>

                    <div>
                        <h1>Export Milestones</h1>
                        <p>
                            Select, organize and customize the columns for your export.
                        </p>
                    </div>
                </div>

                <Button
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate(ROUTE_MILESTONES)}
                >
                    Back to Milestones
                </Button>
            </div>

            <div className="milestone-export-screen">
                <Row gutter={[20, 20]} className="milestone-export-panels">
                    {/* AVAILABLE COLUMNS */}
                    <Col xs={24} md={12}>
                        <div className="milestone-export-panel">
                            <div className="milestone-export-panel-header">
                                <div>
                                    <h3>Available Columns</h3>
                                    <span>{availableExportColumns.length} columns</span>
                                </div>

                                <Button
                                    type="link"
                                    onClick={selectAllExportColumns}
                                    disabled={availableExportColumns.length === 0}
                                >
                                    Select All
                                </Button>
                            </div>

                            <div className="milestone-export-panel-search">
                                <Input
                                    allowClear
                                    placeholder="Search available columns..."
                                    value={availableSearch}
                                    onChange={(event) => setAvailableSearch(event.target.value)}
                                />
                            </div>

                            <div
                                className="milestone-export-column-list"
                                onDragOver={handleDragOver}
                                onDrop={handleDropOnAvailable}
                            >
                                {filteredAvailableExportColumns.length > 0 ? (
                                    filteredAvailableExportColumns.map((column) => (
                                        <div
                                            className={`milestone-export-column-item ${draggedColumnKey === column.key
                                                    ? "milestone-export-column-dragging"
                                                    : ""
                                                }`}
                                            key={column.key}
                                            draggable
                                            onDragStart={(event) =>
                                                handleDragStart(event, column.key, "available")
                                            }
                                            onDragEnd={clearDragState}
                                        >
                                            <MenuOutlined className="milestone-export-drag-icon" />

                                            <span>{column.label}</span>

                                            <Button
                                                type="text"
                                                className="milestone-export-action-button"
                                                icon={<PlusOutlined />}
                                                title={`Add ${column.label}`}
                                                onClick={() => addExportColumn(column.key)}
                                            />
                                        </div>
                                    ))
                                ) : (
                                    <div className="milestone-export-empty">
                                        {availableExportColumns.length === 0 ? (
                                            <>
                                                <CheckCircleOutlined className="milestone-export-empty-icon" />
                                                <h4>All columns selected</h4>
                                                <p>
                                                    All available columns are already included in your
                                                    export.
                                                </p>
                                            </>
                                        ) : (
                                            <Empty
                                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                description="No matching columns"
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </Col>

                    {/* ACTIVE COLUMNS */}
                    <Col xs={24} md={12}>
                        <div className="milestone-export-panel">
                            <div className="milestone-export-panel-header">
                                <div>
                                    <h3>Active Columns</h3>
                                    <span>
                                        {activeExportColumns.length} columns selected
                                    </span>
                                </div>

                                <Button
                                    type="link"
                                    onClick={removeAllExportColumns}
                                    disabled={activeExportColumns.length === 0}
                                >
                                    Remove All
                                </Button>
                            </div>

                            <div className="milestone-export-panel-search">
                                <Input
                                    allowClear
                                    placeholder="Search active columns..."
                                    value={activeSearch}
                                    onChange={(event) => setActiveSearch(event.target.value)}
                                />
                            </div>

                            <div
                                className="milestone-export-column-list"
                                onDragOver={handleDragOver}
                                onDrop={(event) => handleDropOnActive(event)}
                            >
                                {filteredActiveExportColumns.length > 0 ? (
                                    filteredActiveExportColumns.map((column) => (
                                        <div
                                            className={`milestone-export-column-item ${draggedColumnKey === column.key
                                                    ? "milestone-export-column-dragging"
                                                    : ""
                                                }`}
                                            key={column.key}
                                            draggable
                                            onDragStart={(event) =>
                                                handleDragStart(event, column.key, "active")
                                            }
                                            onDragOver={handleDragOver}
                                            onDrop={(event) =>
                                                handleDropOnActive(event, column.key)
                                            }
                                            onDragEnd={clearDragState}
                                        >
                                            <MenuOutlined className="milestone-export-drag-icon" />

                                            <span>{column.label}</span>

                                            <Button
                                                type="text"
                                                className="milestone-export-action-button"
                                                icon={<CloseOutlined />}
                                                title={`Remove ${column.label}`}
                                                onClick={() => removeExportColumn(column.key)}
                                            />
                                        </div>
                                    ))
                                ) : (
                                    <div className="milestone-export-empty">
                                        <Empty
                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                            description="Drag columns here or add them from Available Columns"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </Col>
                </Row>

                {/* FOOTER */}
                <div className="milestone-export-footer">
                    <div className="milestone-export-selection-summary">
                        <span className="milestone-export-selection-count">
                            {activeExportColumns.length}
                        </span>

                        <span>
                            {activeExportColumns.length === 1
                                ? "column selected"
                                : "columns selected"}
                        </span>
                    </div>

                    <div className="milestone-export-footer-actions">
                        <Button
                            onClick={() => navigate(ROUTE_MILESTONES)}
                            disabled={exporting}
                        >
                            Cancel
                        </Button>

                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            disabled={activeExportColumns.length === 0}
                            loading={exporting}
                            onClick={exportRows}
                        >
                            Export CSV
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExportMilestonesPage;